import {
  TTSTagStatusDBEnum,
  TTSTagStatusEnum,
} from "../../providers/TTSFileStatusProvider";

const textOf = (tag) => tag?.accessibilityFeatures?.[0]?.text ?? "";

// Computes which TTS tag IDs the backend should regenerate when "Retry TTS" is
// toggled on. Returns null when ttsGenerate is false (no constraint — clone
// flows and first-time generation send null to mean "all tags").
//
// A tag is included when:
//   - it is new (no saved counterpart), or
//   - its text was edited, or
//   - the latest live status is FAILED, or
//   - the persisted DB status is FAILED, or
//   - the prior batch ended terminal-failed AND we have NO per-tag signal for
//     this specific tag (last-resort fallback when the backend gave us nothing
//     to scope by).
//
// A tag is NEVER included when there's positive evidence it succeeded
// (live GENERATED or DB GENERATED). The batch-terminal fallback must not
// override a known success — a backend that emits one batch FAILED on top of
// genuine per-tag GENERATEDs would otherwise drag every successful tag into
// the next retry.
export const getTTSTagIdsToRegenerate = ({
  ttsGenerate,
  nextData,
  savedObj,
  liveMap,
}) => {
  if (!ttsGenerate) return null;

  const savedTags = savedObj?.accessibilityElements ?? [];
  const nextTags = nextData?.accessibilityElements ?? [];
  // Read DB statuses from the form (kept fresh by useTtsStatusReconciler)
  // rather than the store. The store carries the optimistic `processing`
  // overlay applied at save time and is never reverted when the batch
  // ultimately fails, so a store read would hide the still-failed rows.
  const dbStatuses = (nextData?.ttsTagStatuses ?? []).filter(Boolean);
  const liveMapResolved = liveMap ?? {};
  const batchTerminalFailed = ["FAILED", "STUCK"].includes(
    liveMapResolved["__requestStatus"]?.status,
  );

  const savedByIdentifier = new Map(
    savedTags.map((tag) => [tag.identifier, tag]),
  );
  const dbStatusByTagId = new Map(
    dbStatuses.map(({ tagId, status }) => [tagId, status]),
  );

  return nextTags
    .filter((tag) => {
      if (!tag?.identifier) return false;

      const liveStatus = liveMapResolved[tag.identifier]?.status;
      const dbStatus = dbStatusByTagId.get(tag.identifier);

      // Positive evidence of success — never include, even if the batch
      // terminal was FAILED. This is the precise bug the old code had:
      // batchTerminalFailed was treated as authoritative for *every* tag,
      // which dragged GENERATED tags into the retry list.
      // Live success is `GENERATED`; DB success is either `SAVED` (legacy
      // clone/migration paths) or `GENERATED` (current backend).
      if (liveStatus === TTSTagStatusEnum.GENERATED) return false;
      if (
        dbStatus === TTSTagStatusDBEnum.SAVED ||
        dbStatus === TTSTagStatusDBEnum.GENERATED
      )
        return false;

      const savedTag = savedByIdentifier.get(tag.identifier);
      // New tag (no prior DB row).
      if (!savedTag) return true;
      // Edited text — needs a fresh mp3.
      if (textOf(savedTag) !== textOf(tag)) return true;
      // Latest live attempt failed.
      if (liveStatus === TTSTagStatusEnum.FAILED) return true;
      // DB row records the last attempt as failed.
      if (dbStatus === TTSTagStatusDBEnum.FAILED) return true;
      // Batch terminal-failed fallback — kicks in for tags whose only DB
      // signal is non-terminal (no row, or a stale optimistic `processing` /
      // `enqueued` from a reconciler refetch that was canceled by a follow-up
      // mutation). The earlier exclusions for liveStatus=GENERATED and
      // dbStatus=SAVED/GENERATED still apply — positive evidence of success
      // always wins, so this rule can only resurrect tags we have no
      // definitive outcome for.
      const dbStatusIsNonTerminal =
        !dbStatus ||
        dbStatus === TTSTagStatusDBEnum.PROCESSING ||
        dbStatus === TTSTagStatusDBEnum.ENQUEUED;
      if (batchTerminalFailed && !liveStatus && dbStatusIsNonTerminal)
        return true;
      return false;
    })
    .map((tag) => tag.identifier);
};
